import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { AlertTriangle, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { isSilentError } from '@/utils/logoutGuard';
import { startPolling } from '@/utils/layoutPolling';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ALLOWED_ROLES = ['admin', 'marketing', 'supervisor', 'super_admin'];

export default function PendingOTWCancelBanner({ onHeightChange, fixedMobile = true }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const bannerRef = useRef(null);

  const isAllowedRole = ALLOWED_ROLES.includes(user?.role);

  const fetchPendingOtwCancel = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return true;
      const response = await axios.get(`${API}/orders/pending-otw-cancel`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data?.items || []);
      return true;
    } catch (error) {
      // Gagal diam-diam: banner tidak berubah di putaran ini. false memicu jeda lebih lama.
      return isSilentError(error) ? true : false;
    }
  }, []);

  useEffect(() => {
    if (!isAllowedRole) return;
    // Okt 2026 (Fase 6): polling lewat startPolling (putaran berikutnya baru
    // dijadwalkan setelah putaran selesai, berhenti saat tab tersembunyi/offline,
    // jeda melebar saat gagal). Fetch pertama tetap ditunda 900ms agar tidak
    // berebut dengan gelombang fetch awal AdminLayout.
    let stop = null;
    const startTimer = setTimeout(() => {
      stop = startPolling(() => fetchPendingOtwCancel(), { intervalMs: 30000 });
    }, 900);

    return () => {
      clearTimeout(startTimer);
      if (stop) stop();
    };
  }, [isAllowedRole, fetchPendingOtwCancel]);

  // Clamp active index when the list shrinks (e.g. after nakes withdraws
  // their request, or Admin evaluates it).
  useEffect(() => {
    if (activeIndex >= items.length && items.length > 0) {
      setActiveIndex(0);
    }
  }, [items, activeIndex]);

  // Report rendered height to the parent layout so it can push
  // MobileStickyHeader down and pad content — banner is `fixed` on mobile,
  // so it no longer reserves flow space on its own.
  useEffect(() => {
    if (items.length === 0 && onHeightChange) onHeightChange(0);
  }, [items.length, onHeightChange]);

  useLayoutEffect(() => {
    const el = bannerRef.current;
    if (!el || !onHeightChange) return;
    const report = () => onHeightChange(el.offsetHeight);
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onHeightChange, items.length]);

  // No manual dismiss — banner only disappears once the fetch returns an
  // empty list (request withdrawn or evaluated).
  if (!isAllowedRole || items.length === 0) return null;

  const current = items[Math.min(activeIndex, items.length - 1)];

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % items.length);
  };

  const handleViewOrder = () => {
    if (user?.role === 'marketing') {
      navigate(`/marketing/orders/${current.order_id}`);
    } else {
      navigate(`/admin/orders/${current.order_id}`);
    }
  };

  let timeAgo = '';
  if (current.waktu_pengajuan) {
    try {
      timeAgo = formatDistanceToNow(new Date(current.waktu_pengajuan), { addSuffix: true, locale: idLocale });
    } catch {
      timeAgo = '';
    }
  }

  return (
    <div
      ref={bannerRef}
      className={`${fixedMobile ? 'fixed lg:sticky' : 'sticky'} top-0 left-0 right-0 z-[60] w-full bg-orange-500 border-b-2 border-orange-600 px-4 py-2.5`}
      data-testid="pending-otw-cancel-banner"
    >
      <div className="flex flex-wrap items-center gap-3 max-w-screen-2xl mx-auto">
        <div className="flex items-center gap-2 flex-shrink-0 text-white">
          <AlertTriangle className="w-5 h-5" />
          <span className="text-xs font-bold uppercase tracking-wide">PENGAJUAN PEMBATALAN OTW</span>
        </div>

        <div className="flex-1 min-w-0 text-sm text-white truncate">
          <span className="font-semibold">#{current.order_id}</span>
          {' · '}
          <span>Nakes: {current.nakes_name || 'N/A'}</span>
          {' · '}
          <span>Alasan: {current.alasan_cancel || 'N/A'}</span>
          {timeAgo && (
            <span className="inline-flex items-center gap-1 ml-2 text-orange-100 whitespace-nowrap">
              <Clock className="w-3 h-3" />
              {timeAgo}
            </span>
          )}
        </div>

        {items.length > 1 && (
          <div className="flex items-center gap-1 flex-shrink-0 text-xs text-white">
            <button
              type="button"
              onClick={handlePrev}
              aria-label="Sebelumnya"
              className="p-1 rounded hover:bg-white/20"
              data-testid="pending-otw-cancel-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span>{activeIndex + 1} dari {items.length}</span>
            <button
              type="button"
              onClick={handleNext}
              aria-label="Selanjutnya"
              className="p-1 rounded hover:bg-white/20"
              data-testid="pending-otw-cancel-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={handleViewOrder}
          className="flex-shrink-0 text-xs font-semibold bg-white/15 hover:bg-white/25 text-white px-3 py-1.5 rounded-lg transition-colors"
          data-testid="pending-otw-cancel-view-order"
        >
          Lihat Order
        </button>
      </div>
    </div>
  );
}
